import WebpageService from '../services/webpage.service';
import DataService from '@shared/services/data.service';
import { normalizeUrlForDedup } from '../../WebCrawl/helpers/crawl-validation';

/** Same cap as WebpageAdd Multiples — backend also enforces this. */
export const MAX_BATCH_BASELINE_SIZE = 200;

/**
 * Loads current per-plan URL limits from the user profile (`plan_usage`: limit, count per plan type).
 * Used before and inside the batch loop so quota stays in sync with the server (e.g. other tabs or prior batches).
 */
export async function fetchPlanUsage() {
  const userData = await DataService.getUsers();
  return userData.plan_usage || {};
}

/**
 * Drops duplicate URLs so the same URL is not sent twice in one batch.
 * Uses spec-correct normalization: scheme+hostname are case-insensitive,
 * path/query are case-sensitive.
 */
export function dedupeUrlsForBaseline(urls) {
  if (!Array.isArray(urls) || urls.length === 0) return [];
  const seen = new Set();
  const out = [];
  urls.forEach((raw) => {
    const s = typeof raw === 'string' ? raw.trim() : '';
    if (!s) return;
    const key = normalizeUrlForDedup(s);
    if (seen.has(key)) return;
    seen.add(key);
    out.push(s);
  });
  return out;
}

/**
 * Queues URLs via POST batch_jobs/baseline in chunks, respecting plan quota (same rules as Add Webpage → Multiples).
 *
 * @param {string[]} urls
 * @param {{ plan_type: string, user_agent_id: number, text_string_appear?: string, text_string_not_appear?: string, tag_ids?: number[] }} baseBody
 * @param {Record<string, { limit: number, count: number }>} [planUsageInitial] — current plan_usage; refreshed inside the loop
 * @returns {Promise<{
 *   successfulBatches: { batchId: number, total: number, processed: number }[],
 *   totalUrls: number,
 *   failedBatch: null | { chunkIndex: number, urlCount: number, error: string[], remainingCount: number },
 *   quotaExhausted: boolean,
 *   remainingUrls: string[],
 *   initialQuotaRejected: boolean,
 * }>}
 */
export async function runBatchBaselineForUrls(urls, baseBody, planUsageInitial) {
  const MAX_BATCH_SIZE = MAX_BATCH_BASELINE_SIZE;
  const selectedPlanType = baseBody.plan_type;

  const uniqueUrls = dedupeUrlsForBaseline(urls);

  let planTypes = planUsageInitial || (await fetchPlanUsage());
  const initialPlanUsage = planTypes[selectedPlanType];
  const initialQuota = initialPlanUsage ? initialPlanUsage.limit - initialPlanUsage.count : 0;

  if (initialQuota <= 0) {
    return {
      successfulBatches: [],
      totalUrls: 0,
      failedBatch: null,
      quotaExhausted: false,
      remainingUrls: [...uniqueUrls],
      initialQuotaRejected: true,
    };
  }

  const successfulBatches = [];
  let remainingUrls = [...uniqueUrls];
  let batchIndex = 0;
  let lastError = null;
  let localQuotaUsed = 0;
  let remainingQuota = initialQuota;

  while (remainingUrls.length > 0) {
    const localRemainingQuota = initialQuota - localQuotaUsed;
    planTypes = await fetchPlanUsage();
    const planUsage = planTypes[selectedPlanType];
    const serverRemainingQuota = planUsage ? planUsage.limit - planUsage.count : 0;
    remainingQuota = Math.min(localRemainingQuota, serverRemainingQuota);

    if (remainingQuota <= 0) {
      if (successfulBatches.length === 0) {
        return {
          successfulBatches: [],
          totalUrls: 0,
          failedBatch: null,
          quotaExhausted: false,
          remainingUrls: [...remainingUrls],
          initialQuotaRejected: true,
        };
      }
      break;
    }

    const batchSize = Math.min(remainingUrls.length, MAX_BATCH_SIZE, remainingQuota);
    const batchUrls = remainingUrls.slice(0, batchSize);
    batchIndex += 1;

    try {
      const response = await WebpageService.createMultiples({ ...baseBody, urls: batchUrls });
      const processedCount = response.batch_baseline_urls ? response.batch_baseline_urls.length : batchUrls.length;
      successfulBatches.push({
        batchId: response.id,
        total: batchUrls.length,
        processed: processedCount,
      });
      remainingUrls = remainingUrls.slice(batchSize);
      localQuotaUsed += batchSize;
    } catch (error) {
      lastError = {
        chunkIndex: batchIndex,
        urlCount: batchUrls.length,
        error: error?.messages || ['Unknown error occurred'],
        remainingCount: remainingUrls.length,
      };
      break;
    }
  }

  const quotaExhausted = remainingUrls.length > 0 && remainingQuota <= 0 && !lastError;
  const totalUrls = successfulBatches.reduce((sum, batch) => sum + batch.total, 0);

  return {
    successfulBatches,
    totalUrls,
    failedBatch: lastError,
    quotaExhausted,
    remainingUrls,
    initialQuotaRejected: false,
  };
}
